'use client';

/**
 * User Permissions Hook
 *
 * Provides permission checking for UI components.
 * Uses session-provided permissions from the auth layer.
 *
 * @module useUserPermissions
 */

import { useMemo } from 'react';

/**
 * **`AuthProvider` の認証状態から読む**（Issue #8187）。
 *
 * ここは以前 `@/hooks/use-auth`（NextAuth の `useSession`）を見ていた。ワークスペースや組織を
 * 切り替えると、サーバのセッションは切り替え先になるのに**クライアントが持つセッションだけが
 * 古いまま**で、権限と有料判定が前の断面のまま残っていた（#8176 でサイドバーの名前を直したが、
 * 同じフックの中で呼ぶこちらは古いままで、名前は新・権限は旧という混在になっていた）。
 *
 * `AuthProvider` の `authenticatedUser` は切り替えのたびに `/api/auth/me` を引き直して更新される。
 */
import { useAuth } from '@/components/AuthProvider';

import type { AuthenticatedUser, Workspace } from '@/lib/auth/types';
import type { WorkspaceMembership } from '@/lib/database/operations/workspaces';

/**
 * User permission state
 */
export interface UserPermissions {
  /** Whether the user has edit or create capabilities */
  isPaid: boolean;

  /** Whether the user can create new pages */
  canCreatePages: boolean;

  /** Whether the user can edit existing pages */
  canEditPages: boolean;

  /** Whether the user can delete pages */
  canDeletePages: boolean;

  /** Whether the user can share pages */
  canSharePages: boolean;

  /** Whether the user can start realtime recording */
  canStartRecording: boolean;

  /** Whether the user can manage workspace settings */
  canManageWorkspace: boolean;

  /** Whether the user can invite members */
  canInviteMembers: boolean;

  /** Whether the user can post comments */
  canComment: boolean;

  /** Whether the user has access to personal workspace */
  hasPersonalWorkspace: boolean;
}

/**
 * Calculate permissions from session-provided user permissions.
 * Applies workspace frozen check on top.
 */
function calculatePermissions(
  user: AuthenticatedUser | null,
  workspace?: Workspace | null,
): UserPermissions {
  if (!user?.permissions) {
    return {
      isPaid: false,
      canCreatePages: false,
      canEditPages: false,
      canDeletePages: false,
      canSharePages: false,
      canStartRecording: false,
      canManageWorkspace: false,
      canInviteMembers: false,
      canComment: false,
      hasPersonalWorkspace: false,
    };
  }

  const p = user.permissions;
  const isFrozen = workspace?.isFrozen ?? false;

  return {
    isPaid: p.canEditPages || p.canCreatePages,
    canCreatePages: !isFrozen && p.canCreatePages,
    canEditPages: !isFrozen && p.canEditPages,
    canDeletePages: !isFrozen && p.canDeletePages,
    canSharePages: !isFrozen && p.canSharePages,
    canStartRecording: p.canStartRecording,
    canManageWorkspace: !isFrozen && p.canManageWorkspace,
    canInviteMembers: !isFrozen && p.canInviteMembers,
    canComment: !isFrozen && p.canComment,
    hasPersonalWorkspace: p.canCreatePages,
  };
}

/**
 * Hook to get current user's permissions
 *
 * @param workspace - Optional workspace context
 * @param membership - Optional workspace membership (unused, kept for API compatibility)
 * @returns User permissions object
 *
 * @example
 * ```tsx
 * function PageEditor() {
 *   const { canEditPages } = useUserPermissions();
 *
 *   if (!canEditPages) {
 *     return <UpgradePrompt />;
 *   }
 *
 *   return <Editor />;
 * }
 * ```
 */
export function useUserPermissions(
  workspace?: Workspace | null,
  _membership?: WorkspaceMembership | null
): UserPermissions {
  const { authenticatedUser } = useAuth();

  return useMemo(() => {
    return calculatePermissions(authenticatedUser, workspace);
  }, [authenticatedUser, workspace]);
}

/**
 * Hook to check if user can perform a specific action
 *
 * @param action - The action to check
 * @param workspace - Optional workspace context
 * @returns Whether the user can perform the action
 *
 * @example
 * ```tsx
 * function CreateButton() {
 *   const canCreate = useCanPerform('create', workspace);
 *   return canCreate ? <Button>Create</Button> : null;
 * }
 * ```
 */
export function useCanPerform(
  action: 'create' | 'edit' | 'delete' | 'share' | 'manage' | 'invite',
  workspace?: Workspace | null,
  membership?: WorkspaceMembership | null
): boolean {
  const permissions = useUserPermissions(workspace, membership);

  switch (action) {
    case 'create':
      return permissions.canCreatePages;
    case 'edit':
      return permissions.canEditPages;
    case 'delete':
      return permissions.canDeletePages;
    case 'share':
      return permissions.canSharePages;
    case 'manage':
      return permissions.canManageWorkspace;
    case 'invite':
      return permissions.canInviteMembers;
    default:
      return false;
  }
}

/**
 * Hook to check if current workspace is editable
 *
 * @param workspace - The workspace to check
 * @param membership - Optional workspace membership for role checking
 * @returns Whether the workspace is editable by the current user
 */
export function useIsWorkspaceEditable(
  workspace?: Workspace | null,
  membership?: WorkspaceMembership | null
): boolean {
  const { canEditPages } = useUserPermissions(workspace, membership);

  if (!workspace) return false;
  if (workspace.isFrozen) return false;

  return !!canEditPages;
}

/**
 * Hook to get upgrade prompt information
 *
 * Returns information about what features are locked
 * and what the user needs to do to unlock them.
 *
 * @returns Upgrade prompt information
 */
export function useUpgradePrompt(): {
  shouldShow: boolean;
  reason: 'not_paid' | 'workspace_frozen' | null;
  message: string;
} {
  const { authenticatedUser } = useAuth();
  const permissions = authenticatedUser?.permissions;
  const hasPaidCapability = permissions?.canEditPages || permissions?.canCreatePages;
  const workspace = authenticatedUser && 'workspace' in authenticatedUser ? authenticatedUser.workspace : undefined;

  if (!hasPaidCapability) {
    return {
      shouldShow: true,
      reason: 'not_paid',
      message: '編集権限がありません',
    };
  }

  if (workspace?.isFrozen) {
    return {
      shouldShow: true,
      reason: 'workspace_frozen',
      message: 'このワークスペースは凍結されています。管理者にお問い合わせください',
    };
  }

  return {
    shouldShow: false,
    reason: null,
    message: '',
  };
}
